import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'insidePanama_theme';
const EVENT = 'theme-change';

// Theme system : 'light' | 'dark' | 'system'.
//   - 'light' / 'dark' = user-pinned preference, sticks across sessions
//   - 'system' (default) = follows the OS preference via prefers-color-scheme
//
// The actual class on <html> is always either 'dark' or absent, because
// Tailwind's darkMode: 'class' config keys on .dark. We compute the
// "effective" theme from the user choice + system preference at runtime.

function getStoredChoice() {
  if (typeof window === 'undefined') return 'system';
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch { /* private mode */ }
  return 'system';
}

function systemPrefersDark() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

function applyToDocument(effective) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  // Flip iOS Safari URL bar + Android Chrome tab colour to match the
  // page edge — keeps the transition seamless (no white strip above a
  // black page on dark mode).
  const meta = document.querySelector('meta[name="theme-color"]');
  if (effective === 'dark') {
    root.classList.add('dark');
    root.style.colorScheme = 'dark';
    if (meta) meta.setAttribute('content', '#000000');
  } else {
    root.classList.remove('dark');
    root.style.colorScheme = 'light';
    if (meta) meta.setAttribute('content', '#F2EDE3');
  }
}

/**
 * Reactive theme hook with three-state choice + live OS-preference tracking.
 *
 * Returns :
 *   - `choice`     : the user's persisted choice ('light' | 'dark' | 'system')
 *   - `effective`  : what's currently rendered ('light' | 'dark')
 *   - `setChoice`  : commit a new choice (writes to localStorage + applies CSS)
 *
 * Cross-tab sync via the `storage` event + custom `theme-change` event
 * so any mounted hook stays in sync with toggles in other tabs/components.
 *
 * @example
 *   const { effective, setChoice } = useTheme();
 *   <button onClick={() => setChoice(effective === 'dark' ? 'light' : 'dark')}>
 *     {effective === 'dark' ? 'Mode clair' : 'Mode sombre'}
 *   </button>
 */
export function useTheme() {
  const [choice, setChoiceState] = useState(() => getStoredChoice());
  const [systemDark, setSystemDark] = useState(() => systemPrefersDark());

  const effective = choice === 'system' ? (systemDark ? 'dark' : 'light') : choice;

  // Apply on every effective change.
  useEffect(() => {
    applyToDocument(effective);
  }, [effective]);

  // Listen to OS preference changes — fires when user flips the system
  // theme while the tab is open. Only matters when choice === 'system'.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mql = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mql) return undefined;
    const onChange = (e) => setSystemDark(e.matches);
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);

  // Cross-tab sync.
  useEffect(() => {
    const onCustom = () => setChoiceState(getStoredChoice());
    const onStorage = (e) => { if (e.key === STORAGE_KEY) setChoiceState(getStoredChoice()); };
    window.addEventListener(EVENT, onCustom);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT, onCustom);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setChoice = useCallback((next) => {
    if (next !== 'light' && next !== 'dark' && next !== 'system') return;
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* */ }
    setChoiceState(next);
    window.dispatchEvent(new CustomEvent(EVENT));
  }, []);

  return { choice, effective, setChoice };
}

// FOUC-prevention snippet — inline this in <head> BEFORE the bundle loads
// so the .dark class is set before the first paint. Otherwise users see
// a flash of light theme on cold load when their system is dark.
//
// Used by index.html (not the hook). Exported here for documentation
// purposes only — the actual implementation is duplicated as inline JS
// in index.html since the hook can't run before <body>.
export const FOUC_PREVENTION_SCRIPT = `(function(){try{var t=localStorage.getItem('${STORAGE_KEY}');var d=t==='dark'||(t!=='light'&&window.matchMedia('(prefers-color-scheme: dark)').matches);if(d){document.documentElement.classList.add('dark');document.documentElement.style.colorScheme='dark';}}catch(e){}})();`;
